import type { FC, ReactNode } from 'react'

import { Atom, Orbit, Target, Waves } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { Accent, SectionHeading } from '@/components/landing/section-heading'

import { steps } from '@/constants/landing'

const PickVisual: FC = (): ReactNode => {
    const rows = [
        { icon: Target, title: "Projectile motion", tag: "Mechanics", active: false },
        { icon: Orbit, title: "Kepler orbits", tag: "Gravitation", active: true },
        { icon: Waves, title: "Standing waves", tag: "Waves", active: false },
        { icon: Atom, title: "Quantum tunnelling", tag: "Quantum", active: false }
    ]

    return (
        <ul className='flex flex-col gap-1.5'>
            {rows.map(row => (
                <li
                    key={row.title}
                    className={cn(
                        'flex items-center gap-3 rounded-xl border px-3 py-2.5 text-sm transition-colors',
                        row.active ? 'border-brand/30 bg-brand-soft text-ink' : 'border-transparent bg-secondary/70 text-muted-foreground'
                    )}
                >
                    <row.icon className={cn('size-4 shrink-0', row.active ? 'text-brand' : 'text-muted-foreground')} />
                    <span className='flex-1 font-medium'>{row.title}</span>
                    <span className={cn('font-mono text-[10px] uppercase tracking-[0.12em]', row.active ? 'text-brand' : 'text-muted-foreground/80')}>
                        {row.active ? "Open" : row.tag}
                    </span>
                </li>
            ))}
        </ul>
    )
}

const LeverVisual: FC = (): ReactNode => {
    return (
        <div className='flex flex-col gap-4'>
            <div>
                <div className='flex justify-between text-xs'>
                    <span className='text-muted-foreground'>Mass</span>
                    <span className='font-mono text-ink'>2.0 kg</span>
                </div>
                <div className='relative mt-2 h-1 rounded-full bg-border'>
                    <span className='absolute inset-y-0 left-0 w-[36%] rounded-full bg-ink/70' />
                    <span className='absolute top-1/2 left-[36%] size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-input bg-white shadow' />
                </div>
            </div>

            <div>
                <div className='flex justify-between text-xs'>
                    <span className='text-muted-foreground'>Gravity</span>
                    <span className='font-mono text-brand'>adjusting</span>
                </div>
                <div className='relative mt-2 h-1 rounded-full bg-border'>
                    <span className='slide-demo-fill absolute inset-y-0 left-0 w-[18%] rounded-full bg-brand' />
                    <span className='slide-demo absolute top-1/2 left-[18%] size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border border-brand/40 bg-white shadow-[0_0_0_4px_rgba(47,73,255,0.12)]' />
                </div>
            </div>

            <div>
                <div className='flex justify-between text-xs'>
                    <span className='text-muted-foreground'>Time</span>
                    <span className='font-mono text-ink'>× 0.5</span>
                </div>
                <div className='relative mt-2 h-1 rounded-full bg-border'>
                    <span className='absolute inset-y-0 left-0 w-[25%] rounded-full bg-ink/70' />
                    <span className='absolute top-1/2 left-[25%] size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-input bg-white shadow' />
                </div>
            </div>
        </div>
    )
}

const MathVisual: FC = (): ReactNode => {
    return (
        <div>
            <p className='font-serif text-[40px] leading-none text-ink'>
                <span className='italic'>F</span> = <span className='rounded-md bg-brand-soft px-1.5 italic text-brand'>m</span>
                <span className='mx-1 text-muted-foreground'>·</span>
                <span className='rounded-md bg-signal-soft px-1.5 italic text-signal'>a</span>
            </p>

            <div className='mt-5 flex flex-wrap gap-2 font-mono text-[11px]'>
                <span className='rounded-full bg-brand-soft px-2.5 py-1 text-brand'>m = 2.0 kg</span>
                <span className='rounded-full bg-signal-soft px-2.5 py-1 text-signal'>a = 4.9 m/s²</span>
                <span className='rounded-full bg-secondary px-2.5 py-1 text-ink'>F = 9.8 N</span>
            </div>

            <svg viewBox='0 0 240 56' className='mt-5 h-14 w-full' aria-hidden='true'>
                <line x1='0' y1='28' x2='240' y2='28' stroke='var(--border)' strokeDasharray='2 4' />
                <path
                    d='M0 28 C 20 4, 40 4, 60 28 S 100 52, 120 28 S 160 4, 180 28 S 220 52, 240 28'
                    fill='none'
                    stroke='var(--brand)'
                    strokeWidth='1.8'
                    strokeLinecap='round'
                    pathLength={1}
                    className='draw-path'
                />
            </svg>
        </div>
    )
}

const visuals = [PickVisual, LeverVisual, MathVisual]

export const HowItWorksSection: FC = (): ReactNode => {
    return (
        <section id='how-it-works' className='scroll-mt-20 border-t border-hairline py-24 sm:py-32'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <Reveal>
                    <SectionHeading
                        index="03"
                        label="How it works"
                        align='center'
                        title={<>From intuition to equation in <Accent>three moves.</Accent></>}
                        description="Inertia is built around one loop: see something happen, change why it happens, then read the math that explains it."
                    />
                </Reveal>

                <ol className='mt-16 grid gap-4 md:grid-cols-3 lg:gap-5'>
                    {steps.map((step, index) => {
                        const Visual = visuals[index]

                        return (
                            <li key={step.number}>
                                <Reveal delay={index * 110} className='h-full'>
                                    <article className='flex h-full flex-col rounded-[28px] border border-border/80 bg-white p-6 elevate sm:p-7'>
                                        <div className='flex items-center justify-between'>
                                            <span className='font-mono text-[11px] uppercase tracking-[0.16em] text-brand'>
                                                Step {step.number}
                                            </span>

                                            <span className='font-serif text-4xl italic leading-none text-border'>
                                                {step.number}
                                            </span>
                                        </div>

                                        <div className='mt-6 min-h-[188px] rounded-2xl border border-hairline bg-white p-4'>
                                            <Visual />
                                        </div>

                                        <h3 className='mt-7 text-xl font-semibold tracking-[-0.02em] text-ink'>
                                            {step.title}
                                        </h3>

                                        <p className='mt-2 text-[15px] leading-relaxed text-muted-foreground'>
                                            {step.description}
                                        </p>
                                    </article>
                                </Reveal>
                            </li>
                        )
                    })}
                </ol>
            </div>
        </section>
    )
}
